/**
 * DeployHistory.js
 *
 * Página del Admin Panel que muestra el historial de deploys a producción.
 * Permite ver qué versión está desplegada y el historial de cambios.
 */
import React, { useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { initFirebase } from "../../firebaseConfig";
import { signInWithGoogle, signOutUser } from "../../services/api/modules/auth";
import "bootstrap/dist/css/bootstrap.min.css";

function DeployHistory() {
  const [adminUser, setAdminUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [deploys, setDeploys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Autenticación
  useEffect(() => {
    let unsubscribe;

    async function setupAuth() {
      try {
        const { auth } = await initFirebase();

        unsubscribe = onAuthStateChanged(auth, (user) => {
          setAdminUser(user);
          setAuthLoading(false);

          if (user) {
            cargarHistorial(user);
          }
        });
      } catch (error) {
        console.error("Error inicializando auth:", error);
        setAuthLoading(false);
      }
    }

    setupAuth();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Cargar historial de deploys
  const cargarHistorial = async (user) => {
    setLoading(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/deploy-history?limit=100`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error al cargar historial");
      }

      const data = await response.json();

      if (data.success) {
        setDeploys(data.data || []);
      } else {
        throw new Error(data.error || "Error desconocido");
      }
    } catch (err) {
      console.error("Error cargando historial:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Formatear fecha
  const formatearFecha = (fechaISO) => {
    if (!fechaISO) return "N/A";
    try {
      return new Date(fechaISO).toLocaleString("es-MX", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return fechaISO;
    }
  };

  // Loading de autenticación
  if (authLoading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Verificando sesión...</span>
        </div>
        <p className="mt-3">Verificando sesión...</p>
      </div>
    );
  }

  // Sin autenticación
  if (!adminUser) {
    return (
      <div className="container mt-5 text-center">
        <h2 className="mb-3">Historial de Deploys - Acceso Restringido</h2>
        <button
          onClick={() => signInWithGoogle().catch(console.error)}
          className="btn btn-warning"
        >
          Iniciar Sesión con Google
        </button>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <div className="bg-white border-bottom sticky-top">
        <div className="container-fluid p-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <a href="/admin-operativo" className="btn btn-outline-secondary btn-sm">
                <i className="bi bi-arrow-left"></i> Volver
              </a>
              <h4 className="mb-0">
                <i className="bi bi-clock-history me-2"></i>
                Historial de Deploys
              </h4>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <span className="text-muted small">{adminUser.email}</span>
              <button onClick={signOutUser} className="btn btn-outline-danger btn-sm">
                <i className="bi bi-box-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="container-fluid p-4">
        {/* Último deploy destacado */}
        {deploys.length > 0 && (
          <div className="card mb-4 border-success">
            <div className="card-header bg-success text-white">
              <i className="bi bi-check-circle me-2"></i>
              Último Deploy a Producción
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <small className="text-muted d-block">Fecha</small>
                  <strong>{formatearFecha(deploys[0].fecha)}</strong>
                </div>
                <div className="col-md-2">
                  <small className="text-muted d-block">Rama</small>
                  <code className="text-primary">{deploys[0].rama}</code>
                </div>
                <div className="col-md-2">
                  <small className="text-muted d-block">Commit</small>
                  <code className="bg-light px-2 py-1 rounded">{deploys[0].commit}</code>
                </div>
                <div className="col-md-5">
                  <small className="text-muted d-block">Descripción</small>
                  <span>{deploys[0].descripcion || "Sin descripción"}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
            <button
              className="btn btn-link btn-sm"
              onClick={() => cargarHistorial(adminUser)}
            >
              Reintentar
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        )}

        {/* Tabla de historial */}
        {!loading && deploys.length > 0 && (
          <div className="card">
            <div className="card-header">
              <i className="bi bi-list-ul me-2"></i>
              Historial Completo ({deploys.length} deploys)
            </div>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "180px" }}>Fecha</th>
                    <th style={{ width: "120px" }}>Ambiente</th>
                    <th style={{ width: "200px" }}>Rama</th>
                    <th style={{ width: "100px" }}>Commit</th>
                    <th>Descripción</th>
                  </tr>
                </thead>
                <tbody>
                  {deploys.map((deploy, index) => (
                    <tr key={deploy.id || index}>
                      <td>
                        <small>{formatearFecha(deploy.fecha)}</small>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            deploy.ambiente === "PROD"
                              ? "bg-danger"
                              : deploy.ambiente === "UAT"
                              ? "bg-warning text-dark"
                              : "bg-secondary"
                          }`}
                        >
                          {deploy.ambiente}
                        </span>
                      </td>
                      <td>
                        <code className="text-primary small">{deploy.rama}</code>
                      </td>
                      <td>
                        <code className="bg-light px-2 py-1 rounded small">
                          {deploy.commit}
                        </code>
                      </td>
                      <td>
                        <span className="text-truncate d-inline-block" style={{ maxWidth: "400px" }}>
                          {deploy.descripcion || "-"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Sin deploys */}
        {!loading && deploys.length === 0 && !error && (
          <div className="text-center py-5">
            <i className="bi bi-inbox text-muted" style={{ fontSize: "3rem" }}></i>
            <p className="text-muted mt-3">No hay deploys registrados</p>
            <small className="text-muted">
              Los deploys se registran automáticamente al ejecutar{" "}
              <code>npm run deploy:prod</code>
            </small>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeployHistory;
